import { EditorState, ContentState, convertToRaw, convertFromRaw } from 'draft-js'

// ---------------------------------------------------------------------------
// Corpo armazenado = raw ContentState (JSON) do Draft.js.
// Round-trip sem perdas para edição; conversão própria para HTML no preview.
// ---------------------------------------------------------------------------

export function parseBody(body) {
  if (!body) return null
  if (typeof body === 'object') return body
  try {
    const obj = JSON.parse(body)
    if (obj && Array.isArray(obj.blocks)) return obj
  } catch (e) {
    // não é JSON: trata como texto puro
  }
  return null
}

export function editorStateFromBody(body) {
  const raw = parseBody(body)
  if (raw) {
    try {
      return EditorState.createWithContent(convertFromRaw(raw))
    } catch (e) {
      // cai no fallback de texto puro
    }
  }
  if (body && typeof body === 'string') {
    return EditorState.createWithContent(ContentState.createFromText(body))
  }
  return EditorState.createEmpty()
}

export function bodyFromEditorState(editorState) {
  const content = editorState.getCurrentContent()
  return JSON.stringify(convertToRaw(content))
}

export function plainTextFromBody(body) {
  const raw = parseBody(body)
  if (raw) return raw.blocks.map((b) => b.text).join(' ').trim()
  return String(body || '').trim()
}

// ---------------------------------------------------------------------------
// raw -> HTML
// ---------------------------------------------------------------------------

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

const STYLE_TAGS = {
  BOLD: ['<strong>', '</strong>'],
  ITALIC: ['<em>', '</em>'],
  UNDERLINE: ['<u>', '</u>'],
}

function applyInlineStyles(text, ranges) {
  if (!text) return ''
  const len = text.length
  const styleAt = []
  for (let i = 0; i < len; i += 1) styleAt.push(new Set())

  const list = ranges || []
  for (let r = 0; r < list.length; r += 1) {
    const range = list[r]
    if (STYLE_TAGS[range.style]) {
      const end = Math.min(range.offset + range.length, len)
      for (let i = range.offset; i < end; i += 1) styleAt[i].add(range.style)
    }
  }

  const orderedStyles = ['BOLD', 'ITALIC', 'UNDERLINE']
  const keyOf = (set) => orderedStyles.filter((s) => set.has(s)).join('|')

  let html = ''
  let current = []
  let prevKey = ''
  for (let i = 0; i < len; i += 1) {
    const key = keyOf(styleAt[i])
    if (key !== prevKey) {
      for (let c = current.length - 1; c >= 0; c -= 1) html += STYLE_TAGS[current[c]][1]
      current = orderedStyles.filter((s) => styleAt[i].has(s))
      for (let c = 0; c < current.length; c += 1) html += STYLE_TAGS[current[c]][0]
      prevKey = key
    }
    html += escapeHtml(text[i])
  }
  for (let c = current.length - 1; c >= 0; c -= 1) html += STYLE_TAGS[current[c]][1]
  return html
}

const LIST_STYLE_BY_DEPTH = ['decimal', 'lower-alpha', 'upper-roman', 'lower-roman']

function blockInner(block) {
  return applyInlineStyles(block.text, block.inlineStyleRanges)
}

/** Converte raw ContentState (ou JSON string) para HTML. */
export function rawToHtml(body) {
  const raw = parseBody(body)
  if (!raw) {
    const txt = String(body || '').trim()
    return txt ? `<p>${escapeHtml(txt)}</p>` : ''
  }

  let html = ''
  const listStack = [] // { type: 'ol'|'ul', depth }

  const closeListsTo = (depth) => {
    while (listStack.length && listStack[listStack.length - 1].depth >= depth) {
      const l = listStack.pop()
      html += `</${l.type}>`
    }
  }

  raw.blocks.forEach((block) => {
    const isList = block.type === 'ordered-list-item' || block.type === 'unordered-list-item'
    if (isList) {
      const listType = block.type === 'ordered-list-item' ? 'ol' : 'ul'
      const depth = block.depth || 0
      // fecha listas mais profundas ou de mesmo nível com tipo diferente
      while (
        listStack.length
        && (listStack[listStack.length - 1].depth > depth
          || (listStack[listStack.length - 1].depth === depth && listStack[listStack.length - 1].type !== listType))
      ) {
        const l = listStack.pop()
        html += `</${l.type}>`
      }
      if (!listStack.length || listStack[listStack.length - 1].depth < depth) {
        const styleType = listType === 'ol' ? LIST_STYLE_BY_DEPTH[Math.min(depth, LIST_STYLE_BY_DEPTH.length - 1)] : 'disc'
        html += `<${listType} style="list-style-type:${styleType}">`
        listStack.push({ type: listType, depth })
      }
      html += `<li>${blockInner(block)}</li>`
      return
    }

    closeListsTo(0)

    switch (block.type) {
      case 'header-one': html += `<h1>${blockInner(block)}</h1>`; break
      case 'header-two': html += `<h2>${blockInner(block)}</h2>`; break
      case 'header-three': html += `<h3>${blockInner(block)}</h3>`; break
      case 'blockquote': html += `<blockquote>${blockInner(block)}</blockquote>`; break
      default: {
        const inner = blockInner(block)
        html += inner ? `<p>${inner}</p>` : '<p><br/></p>'
      }
    }
  })
  closeListsTo(0)
  return html
}

// ---------------------------------------------------------------------------
// Placeholders {{campo}}
// ---------------------------------------------------------------------------

const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g

/** Lista de placeholders únicos usados num corpo. */
export function extractPlaceholders(body) {
  const text = plainTextFromBody(body)
  const found = new Set()
  let m
  // eslint-disable-next-line no-cond-assign
  while ((m = PLACEHOLDER_RE.exec(text)) !== null) found.add(m[1])
  return [...found]
}

/** Substitui {{campo}} pelos valores reais de um registro de contrato. */
export function applyPlaceholders(html, contrato) {
  if (!html) return ''
  return html.replace(PLACEHOLDER_RE, (full, campo) => {
    const val = contrato ? contrato[campo] : undefined
    if (val === undefined || val === null || val === '') return full
    return escapeHtml(String(val))
  })
}

/** Para visualização no catálogo: destaca os placeholders como chips. */
export function highlightPlaceholders(html) {
  if (!html) return ''
  return html.replace(PLACEHOLDER_RE, (full, campo) => (
    `<span class="minuta-placeholder" title="${escapeHtml(campo)}">${escapeHtml(full)}</span>`
  ))
}

// ---------------------------------------------------------------------------
// Numeração derivada da hierarquia (paiId + ord) para o preview do modelo
// ---------------------------------------------------------------------------

/**
 * Recebe itens do modelo já ordenados e devolve uma árvore com numeração.
 * Regras:
 *  - blocos e seções ficam no nível raiz (paiId = null)
 *  - seções recebem numeração "N." ; suas cláusulas-filhas "N.M"
 *  - cláusulas avulsas no raiz recebem numeração sequencial própria
 */
export function montarArvoreNumerada(itens) {
  const ordenados = [...(itens || [])].sort(
    (a, b) => Number(a.minutaModItem_ord ?? 0) - Number(b.minutaModItem_ord ?? 0),
  )
  const raiz = ordenados.filter((i) => !i.minutaModItem_paiId)
  const filhosPor = {}
  ordenados.forEach((i) => {
    if (i.minutaModItem_paiId) {
      const k = i.minutaModItem_paiId
      filhosPor[k] = filhosPor[k] || []
      filhosPor[k].push(i)
    }
  })

  let contadorSecao = 0
  let contadorClausRaiz = 0

  return raiz.map((item) => {
    if (item.minutaModItem_tipo === 'secao') {
      contadorSecao += 1
      const numSecao = String(contadorSecao)
      const filhos = (filhosPor[item.minutaModItem_id] || [])
        .sort((a, b) => Number(a.minutaModItem_ord ?? 0) - Number(b.minutaModItem_ord ?? 0))
        .map((f, idx) => ({ item: f, numero: `${numSecao}.${idx + 1}` }))
      return { item, numero: `${numSecao}.`, filhos }
    }
    if (item.minutaModItem_tipo === 'clausula') {
      contadorClausRaiz += 1
      return { item, numero: `${contadorClausRaiz}.`, filhos: [] }
    }
    // bloco
    return { item, numero: '', filhos: [] }
  })
}
